/**
 * Native vault metadata shim — SPEC §8.3 / NFR-NAT-002.
 * Uses SecureVault (Preferences fallback; Keychain/Keystore when native plugin registers).
 */

import { createSecureVault, reportSecureVaultCapabilities, type SecureVaultCapabilities } from "./secureVault";

export interface NativeVaultBridge {
  platform: "web" | "native";
  capabilities: SecureVaultCapabilities;
  setWrappedVault(json: string): Promise<void>;
  getWrappedVault(): Promise<string | undefined>;
}

const VAULT_KEY = "vault.wrapped";

export async function createNativeVaultBridge(): Promise<NativeVaultBridge> {
  const vault = await createSecureVault();
  const capabilities = await vault.capabilities();
  const platform = capabilities.backend === "preferences" || capabilities.backend === "memory" ? "web" : "native";
  return {
    platform,
    capabilities,
    async setWrappedVault(json: string) {
      await vault.set({ key: VAULT_KEY, value: json });
    },
    async getWrappedVault() {
      const { value } = await vault.get({ key: VAULT_KEY });
      return value ?? undefined;
    },
  };
}

/**
 * SEC-STOR: remove the Argon2id-wrapped vault blob from the SecureVault
 * backend (Preferences/Keychain/Keystore or web fallbacks). Secure reset
 * MUST call this — otherwise an offline-brute-forceable ciphertext copy
 * survives the wipe (SPEC §5.5 Data / secure reset).
 */
export async function clearWrappedVault(): Promise<void> {
  const vault = await createSecureVault();
  try {
    await vault.remove({ key: VAULT_KEY });
  } catch {
    /* fail-closed to caller: reload still proceeds */
  }
  try {
    localStorage.removeItem(`veto.secure.${VAULT_KEY}`);
  } catch {
    /* private mode */
  }
}

export { reportSecureVaultCapabilities };
